/**
 * STEP SIX — full circulatory + digestive combo.
 *
 * Puts together everything the student has learned so far:
 *   • Stomach + intestines (Step 4) — gruel → food → waste.
 *   • Lungs + diaphragm + atmosphere/trachea (Step 5) — CO₂ blood → O₂ blood.
 *
 * Left wall is the "body interface" — four ports stacked top-to-bottom:
 *   1. O₂-blood receptacle (the body receives oxygenated blood here).
 *   2. **Mystery spout** — emits [oxygen] only AFTER port 1 receives O₂-blood.
 *      Pedagogically: "the body has used the O₂ and is sending it back so
 *      the intestines can use it." `dependsOn: ['s6-body-o2-in']` is the
 *      first use of the wall-port dep rule (mirrors organ-spout `dependsOn`).
 *   3. Food+O₂+CO₂ receptacle (enriched blood goes back into the body).
 *   4. CO₂ spout (unconditional — body always sends out deoxygenated blood).
 *
 * Solution flow:
 *   gruel (top wall) → stomach → food → intestines.top (direct link)
 *   atmosphere (top wall) ↔ lungs.top (trachea, with diaphragm joined below)
 *   CO₂ (left wall #4) → lungs.left-in → lungs.right-out (O₂ blood) → left wall #1
 *   left wall #2 (mystery O₂ spout, fires when #1 satisfies) → intestines.left-in
 *   intestines.left-out (food+O₂+CO₂) → left wall #3
 *   intestines.bottom (waste) → bottom toilet
 */
import {
  leftReceptacle,
  leftSpout,
  toiletReceptacle,
  topAtmosphere,
  topSpout,
  type LevelConfig,
} from './portHelpers';
import type { MoleculeMix } from '../types';

export const LEVEL_6: LevelConfig = {
  step: 6,
  title: 'STEP SIX',
  instructions:
    'Specimen needs oxygen to process the organic gruel. (See step 4)',
  // Trachea (atmosphere ↔ lungs top) is mandatory — without it the
  // bidirectional atmosphere could be piped straight into an O₂ receptacle,
  // bypassing the lungs. See LevelConfig.requiredLinks.
  requiredLinks: [{ a: 's6-atmos', b: 's6-lungs-top' }],
  ports: [
    // ---------- TOP wall ----------
    // Gruel spout — esophagus.
    topSpout('s6-gruel-in', 7, ['gruel']),
    // Atmosphere bidirectional — trachea endpoint for the lungs.
    topAtmosphere('s6-atmos', 12, ['oxygen']),

    // ---------- LEFT wall (top → bottom) ----------
    // 1. Body receives O₂-blood. Hint shows oxygen. Exact match only.
    leftReceptacle('s6-body-o2-in', 3, ['oxygen']),
    // 2. MYSTERY spout — porthole shown blank; gated on port 1 receiving O₂.
    //    Once O₂-blood arrives at the body, the body "exhales" oxygen the
    //    intestines can use to absorb food.
    {
      ...leftSpout('s6-body-o2-pass', 6, ['oxygen']),
      hideContents: true,
      dependsOn: ['s6-body-o2-in'],
    },
    // 3. Body receives enriched blood (food + O₂ + CO₂). Exact match only —
    //    no acceptsAlso. Any other mix will burst on arrival.
    leftReceptacle('s6-body-enriched-in', 10, ['food', 'oxygen', 'co2']),
    // 4. Body sends out CO₂-blood. Unconditional — always pumping.
    leftSpout('s6-body-co2-out', 13, ['co2']),

    // ---------- BOTTOM wall ----------
    // Toilet — waste from intestines bottom.
    toiletReceptacle('s6-toilet', 17),
  ],
  organPool: [
    // ---------- Stomach (copy of Level 4 stomach, s6- prefix) ----------
    {
      id: 'stomach',
      art: 'stomach',
      cols: 3,
      rows: 3,
      activatesWhen: ['s6-stomach-out'],
      ports: [
        {
          id: 's6-stomach-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['gruel'],
        },
        {
          id: 's6-stomach-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 1, dRow: 2 },
          carries: ['food'],
          shape: 'castellated',
          dependsOn: ['s6-stomach-in'],
        },
      ],
    },
    // ---------- Intestines (copy of Level 4 intestines, s6- prefix) ----------
    {
      id: 'intestines',
      art: 'intestines',
      cols: 4,
      rows: 3,
      activatesWhen: ['s6-int-left-out', 's6-int-bottom'],
      ports: [
        {
          id: 's6-int-top',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['food'],
          acceptsAny: true,
          shape: 'castellated',
        },
        {
          id: 's6-int-left-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 0 },
          carries: ['oxygen'],
          acceptsAny: true,
        },
        {
          id: 's6-int-left-out',
          kind: 'spout',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 1 },
          carries: ['food', 'oxygen', 'co2'],
          transform: (inputs) => {
            const top = inputs.get('s6-int-top') ?? [];
            const sideIn = inputs.get('s6-int-left-in') ?? [];
            const topHasFood = top.includes('food');
            if (sideIn.length === 0 && !topHasFood) return null;
            const out = new Set(sideIn);
            if (topHasFood) {
              out.add('food');
              out.add('co2');
            }
            return Array.from(out);
          },
        },
        {
          id: 's6-int-bottom',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 2, dRow: 3 },
          carries: ['waste'],
          transform: (inputs) => {
            const top = inputs.get('s6-int-top') ?? [];
            return top.includes('food') ? ['waste'] : null;
          },
        },
      ],
    },
    // ---------- Lungs (copy of Level 5 lungs, s6- prefix) ----------
    {
      id: 'lungs',
      art: 'lungs',
      cols: 5,
      rows: 3,
      activatesWhen: ['s6-lungs-right-out'],
      ports: [
        {
          id: 's6-lungs-top',
          kind: 'bidirectional',
          facing: 'N',
          cellOffset: { dCol: 2, dRow: -1 },
          carries: ['oxygen'],
          coneFlipped: true,
          transform: (inputs) => {
            const left = inputs.get('s6-lungs-left-in') ?? [];
            const bottom = inputs.get('s6-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            const tracheaInputArriving = (inputs.get('s6-lungs-top') ?? []).length > 0;
            if (operating && tracheaInputArriving && left.includes('co2')) {
              return ['co2'];
            }
            return null;
          },
        },
        {
          id: 's6-lungs-left-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 0 },
          carries: ['co2'],
          acceptsAny: true,
          validIf: (inputs) => {
            const bottom = inputs.get('s6-lungs-bottom-pulse') ?? [];
            return bottom.includes('pulse');
          },
        },
        {
          id: 's6-lungs-right-out',
          kind: 'spout',
          facing: 'E',
          cellOffset: { dCol: 5, dRow: 0 },
          carries: ['oxygen'],
          transform: (inputs) => {
            const left = inputs.get('s6-lungs-left-in') ?? [];
            const bottom = inputs.get('s6-lungs-bottom-pulse') ?? [];
            const operating = bottom.includes('pulse');
            if (!operating) return null;
            if (left.length === 0) return null;
            const tracheaConnected = (inputs.get('s6-lungs-top') ?? []).length > 0;
            if (!tracheaConnected) {
              return left as MoleculeMix;
            }
            const out = new Set(left.filter((m) => m !== 'co2'));
            out.add('oxygen');
            return Array.from(out);
          },
        },
        {
          id: 's6-lungs-bottom-pulse',
          kind: 'receptacle',
          facing: 'S',
          cellOffset: { dCol: 2, dRow: 3 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
    // ---------- Diaphragm (copy of Level 5 diaphragm, s6- prefix) ----------
    {
      id: 'diaphragm',
      art: 'diaphragm',
      cols: 3,
      rows: 2,
      ports: [
        {
          id: 's6-diaphragm-top',
          kind: 'spout',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['pulse'],
          shape: 'castellated',
        },
      ],
    },
  ],
};
